import {
  Box,
  Button,
  Field,
  Heading,
  Image,
  Input,
  Text,
  VStack
} from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import { useLogin, useRequestMagicLink } from "../../hooks/useAuthQueries"
import { getUserDetails } from "../../services/authApi"
import { getAuthErrorMessage } from "../../utils/authErrors"
import { getPostLoginPath } from "../../utils/postLoginRoute"

type Step = "email" | "method" | "sent"

const Login: React.FC = () => {
  const [step, setStep] = useState<Step>("email")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const { login: authLogin, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const loginMutation = useLogin()
  const magicLinkMutation = useRequestMagicLink()
  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard")
  }, [isAuthenticated, navigate])

  useEffect(() => {
    if (showPassword) passwordRef.current?.focus()
  }, [showPassword])

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault()
    if (email.trim()) setStep("method")
  }

  const handleSendMagicLink = async () => {
    try {
      await magicLinkMutation.mutateAsync({ email })
      setStep("sent")
    } catch {}
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await loginMutation.mutateAsync({ email, password })
      authLogin(response.data.key)
      const details = await getUserDetails()
      if (details.data?.mustChangePassword) {
        navigate("/force-password-change")
        return
      }
      navigate(getPostLoginPath(details.data?.organisation))
    } catch {}
  }

  const handleChangeEmail = () => {
    setStep("email")
    setShowPassword(false)
    setPassword("")
    loginMutation.reset()
    magicLinkMutation.reset()
  }

  const getLoginError = () => {
    if (!loginMutation.error) return null
    return getAuthErrorMessage(loginMutation.error, [], "Sign in failed. Please try again.")
  }

  return (
    <>
      <Box pb={8}>
        <Link to="/">
          <Image w={{ base: 150, md: 200 }} src="/greenflip_logo.png" alt="GreenFlip" />
        </Link>
      </Box>

      <Box
        bg="gray.100"
        px={{ base: 6, md: 8, lg: 12 }}
        py={{ base: 6, md: 8, lg: 10 }}
        borderRadius="3xl"
        w={{ base: "95%", md: "85%", lg: "80%" }}
        maxW="500px"
      >
        {/* Step 1: Email entry */}
        {step === "email" && (
          <>
            <Heading pb={2} size={{ base: "lg", md: "xl" }}>Welcome back!</Heading>
            <Text fontSize="sm" color="gray.500" pb={6}>Enter your email to continue</Text>
            <form onSubmit={handleContinue}>
              <VStack gap={4} align="stretch">
                <Field.Root>
                  <Field.Label fontSize="xs" fontWeight="bold">Email</Field.Label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    bg="white"
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    _focus={{ borderColor: "green.500" }}
                  />
                </Field.Root>
                <Button
                  type="submit"
                  bg="green.800"
                  color="white"
                  _hover={{ bg: "green.900" }}
                  size="lg"
                >
                  Continue
                </Button>
              </VStack>
            </form>
          </>
        )}

        {/* Step 2: Choose method */}
        {step === "method" && (
          <>
            <Heading pb={1} size={{ base: "md", md: "lg" }}>Sign in as</Heading>
            <Box display="flex" alignItems="center" gap={2} pb={6}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">{email}</Text>
              <Button
                variant="plain"
                size="xs"
                color="green.700"
                fontWeight="semibold"
                height="auto"
                minW={0}
                p={0}
                onClick={handleChangeEmail}
              >
                Change
              </Button>
            </Box>

            <VStack gap={3} align="stretch">
              {/* Magic link – primary */}
              {magicLinkMutation.error && (
                <Box bg="red.50" border="1px solid" borderColor="red.200" color="red.800" px={4} py={3} borderRadius="md" fontSize="sm">
                  {getAuthErrorMessage(magicLinkMutation.error, [], "We couldn't send the sign-in link. Please try again.")}
                </Box>
              )}
              <Button
                bg="green.800"
                color="white"
                _hover={{ bg: "green.900" }}
                size="lg"
                onClick={handleSendMagicLink}
                disabled={magicLinkMutation.isPending}
              >
                {magicLinkMutation.isPending ? "Sending…" : "Send me a sign-in link"}
              </Button>
              <Text fontSize="xs" color="gray.500" textAlign="center">
                We'll email you a one-click link. No password needed.
              </Text>

              {/* Divider */}
              <Box display="flex" alignItems="center" gap={3} py={1}>
                <Box flex={1} h="1px" bg="gray.300" />
                <Text fontSize="xs" color="gray.400" fontWeight="medium">or</Text>
                <Box flex={1} h="1px" bg="gray.300" />
              </Box>

              {/* Password – secondary */}
              {!showPassword ? (
                <Button
                  variant="outline"
                  size="lg"
                  borderColor="gray.300"
                  color="gray.700"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => setShowPassword(true)}
                >
                  Sign in with password
                </Button>
              ) : (
                <form onSubmit={handlePasswordSubmit}>
                  <VStack gap={3} align="stretch">
                    {getLoginError() && (
                      <Box bg="red.50" border="1px solid" borderColor="red.200" color="red.800" px={4} py={3} borderRadius="md" fontSize="sm">
                        {getLoginError()}
                      </Box>
                    )}
                    <Field.Root>
                      <Field.Label fontSize="xs" fontWeight="bold">Password</Field.Label>
                      <Input
                        ref={passwordRef}
                        name="password"
                        type="password"
                        placeholder="Enter your password"
                        bg="white"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        _focus={{ borderColor: "green.500" }}
                      />
                    </Field.Root>
                    <Button
                      type="submit"
                      variant="outline"
                      size="lg"
                      borderColor="gray.300"
                      color="gray.700"
                      _hover={{ bg: "gray.50" }}
                      disabled={loginMutation.isPending}
                    >
                      {loginMutation.isPending ? "Signing in…" : "Sign in with password"}
                    </Button>
                  </VStack>
                </form>
              )}
            </VStack>
          </>
        )}

        {/* Step 3: Magic link sent */}
        {step === "sent" && (
          <VStack gap={4} align="stretch">
            <Box fontSize="3xl" textAlign="center">📬</Box>
            <Heading size="md" textAlign="center">Check your inbox</Heading>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              We sent a sign-in link to <strong>{email}</strong>.
            </Text>
            <Box bg="gray.200" h="1px" />
            <VStack gap={2} align="stretch">
              <Text fontSize="xs" color="gray.500" textAlign="center">Can't find it? Check your spam folder, or</Text>
              <Button
                variant="outline"
                size="sm"
                borderColor="gray.300"
                color="gray.700"
                _hover={{ bg: "gray.50" }}
                onClick={() => {
                  setStep("method")
                  setShowPassword(true)
                  magicLinkMutation.reset()
                }}
              >
                Sign in with password instead
              </Button>
              <Button
                variant="plain"
                size="sm"
                color="green.700"
                onClick={() => {
                  magicLinkMutation.reset()
                  setStep("method")
                }}
              >
                Use a different email
              </Button>
            </VStack>
          </VStack>
        )}
      </Box>

      {step === "email" && (
        <Text textAlign="center" pt={8}>
          Forgot password?{" "}
          <Text display="inline" color="green.800" fontWeight="bold">
            <Link to="/reset-password">Reset my password</Link>
          </Text>
          <br />
          Don't have an account?{" "}
          <Text display="inline" color="green.800" fontWeight="bold">
            <Link to="/contact">Contact us</Link>
          </Text>
        </Text>
      )}

      {step === "method" && (
        <Text textAlign="center" pt={8}>
          Forgot password?{" "}
          <Text display="inline" color="green.800" fontWeight="bold">
            <Link to="/reset-password">Reset my password</Link>
          </Text>
        </Text>
      )}
    </>
  )
}

export default Login
